import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { ProductReview } from '@iheartjane/dm-sdk';
import { Flex, Typography, VerifiedIcon } from '@iheartjane/reefer';
import type { PropsWithChildren } from 'react';

import type { ReviewSort } from '@jane/dm/internal';
import { useKioskSpacing } from '@jane/kiosk/hooks';
import type { AlgoliaProduct } from '@jane/search/data-access';
import { Avatar } from '@jane/shared/components';
import { formatTime } from '@jane/shared/util';

import { ProductDetailsSection } from '../ProductDetailsSection/ProductDetailsSection';
import { SortButton } from '../SortButton';
import { StarRating } from '../StarRating/StarRating';

export type ReviewSortOption = { id: ReviewSort; label: string };

export const reviewSortOptions: ReviewSortOption[] = [
  { id: 'recent', label: 'Most Recent' },
  { id: 'rating', label: 'Rating' },
];

export const defaultReviewSort = reviewSortOptions[0];

type BaseProductReviewsSectionProps = {
  currentSort: ReviewSortOption;
  isLoading: boolean;
  product: AlgoliaProduct | MaterializedMenuProduct;
  productReviews: ProductReview[];
  setCurrentSort: (sort: ReviewSortOption) => void;
};

export type ProductReviewsSectionProps =
  PropsWithChildren<BaseProductReviewsSectionProps>;

export const ProductReviewsSection = ({
  children,
  product,
  productReviews,
  isLoading,
  currentSort,
  setCurrentSort,
}: ProductReviewsSectionProps) => {
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  return (
    <ProductDetailsSection
      ariaLabel="verified review section"
      isLoading={isLoading}
      title="Customer reviews"
    >
      <Flex position="relative" flexDirection="column">
        <Flex justifyContent="space-between" alignItems="center">
          <StarRating
            aggregateRating={product.aggregate_rating}
            reviewCount={product.review_count ?? undefined}
          />
          <SortButton
            aria-label="product review sort"
            currentSort={currentSort}
            filled={false}
            condensed
            onSort={setCurrentSort}
            options={reviewSortOptions}
          />
        </Flex>
        {productReviews.map(
          ({ id, comment, nickname, rating, avatar, createdAt }) => (
            <Flex flexDirection="column" key={id} mt={getKioskSpacing(24, 16)}>
              <Flex alignItems="center" justifyContent="space-between">
                <Flex alignItems="center" mb={getKioskSpacing(12, 8)}>
                  <Avatar avatar={avatar ?? 0} size="lg" />
                  <Typography ml={12} variant="small-body-semibold">
                    {nickname}
                  </Typography>
                </Flex>
                <StarRating aggregateRating={rating} isCondensed />
              </Flex>
              <Flex alignItems="center" gap={8} mb={getKioskSpacing(12, 8)}>
                <VerifiedIcon size="sm" />
                <Typography
                  color="text-light"
                  variant={isIpad ? 'body-regular' : 'small-body-regular'}
                >
                  {`Verified purchase, ${formatTime({
                    time: createdAt,
                    outputFormat: 'MMM d, yyyy',
                  })}`}
                </Typography>
              </Flex>
              <Flex>
                <Typography
                  variant={isIpad ? 'body-regular' : 'small-body-regular'}
                >
                  {comment ?? ''}
                </Typography>
              </Flex>
            </Flex>
          )
        )}
        {children}
      </Flex>
    </ProductDetailsSection>
  );
};
